import { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Checkbox,
    Stack,
    CircularProgress,
    TextField,
    Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import SortIcon from "@mui/icons-material/Sort";
import Close from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import CustomTableContainer from "../../../Layout/CustomTableContainer";
import { tokens } from "../../../theme/theme";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import { useProductionRemarkWiseSummary } from "../../../hooks/production/useProductionRemarkWiseSummary";

/* Generate date range between two dates */
const generateDateRange = (startDateStr, endDateStr) => {
    const dates = [];
    try {
        // Parse dates in YYYY-MM-DD format
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);

        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            // Format as DD-MM-YYYY to match CSV format
            const day = String(currentDate.getDate()).padStart(2, '0');
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const year = currentDate.getFullYear();
            dates.push(`${day}-${month}-${year}`);

            currentDate.setDate(currentDate.getDate() + 1);
        }
    } catch (error) {
        console.error("Error generating date range:", error);
    }
    return dates;
};

/* Sample Remark Wise Data Structure */
const sampleRemarkData = [
    { defect: "MACHINE CREASE", dates: { "01-01-2026": { reel_qty: 2000, bundle_qty: 1885 }, "02-01-2026": { reel_qty: 120, bundle_qty: 165 } } },
    { defect: "WRINKLE", dates: { "01-01-2026": { reel_qty: 0, bundle_qty: 60 }, "07-01-2026": { reel_qty: 0, bundle_qty: 60 } } },
    { defect: "WATER MARK", dates: { "01-01-2026": { reel_qty: 40, bundle_qty: 320 }, "03-01-2026": { reel_qty: 40, bundle_qty: 0 } } },
    { defect: "COATING LINE", dates: { "01-01-2026": { reel_qty: 50, bundle_qty: 290 }, "05-01-2026": { reel_qty: 70, bundle_qty: 0 } } },
    { defect: "CALLENDER PUNCHING", dates: { "01-01-2026": { reel_qty: 70, bundle_qty: 0 }, "02-01-2026": { reel_qty: 70, bundle_qty: 0 } } },
];

export default function RemarkWiseProductionReport({ handleBackWinderMachineEntry }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mode = theme.palette.mode;

    const [rows, setRows] = useState([]);
    const { summaryToday, fetchTodayProductionSummary, isLoading } = useProductionRemarkWiseSummary();

    // Date range filters
    const [fromDate, setFromDate] = useState(() =>
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            .toISOString()
            .slice(0, 10)
    );
    const [toDate, setToDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );

    // Load data only on component mount
    useEffect(() => {
        const loadData = async () => {
            let company = localStorage.getItem("companyid");
            await fetchTodayProductionSummary({
                company,
                fromDate,
                toDate,
            });
        };
        loadData();
    }, []); // Empty dependency array - only on mount


    // Sorting and column visibility
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    // All available dates from data
    const [availableDates, setAvailableDates] = useState([]);

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState({
        Defect: true,
        "Reel Qty": true,
        "Bundle Qty": true,
        "Total": true,
    });

    // Table colors
    const tableColors = {
        headerBg: mode === "dark" ? theme.palette.primary[600] || "#1F2A40" : theme.palette.grey[100],
        dateGroup: mode === "dark" ? theme.palette.orangeAccent?.[400] || "#ff8a46" : theme.palette.yellowAccent?.[100] || "#fff7cc",
        defectCol: mode === "dark" ? theme.palette.blueAccent?.[800] || "#1565c0" : theme.palette.blueAccent?.[100] || "#bbdefb",
        totalCol: mode === "dark" ? theme.palette.greenAccent?.[400] || "#3da58a" : theme.palette.greenAccent?.[100] || "#dbf5ee",
        rowBg: mode === "dark" ? theme.palette.grey[800] : "#fafafa",
    };

    /* Transform API data and prepare table structure */
    useEffect(() => {
        if (!summaryToday || isLoading) return;

        try {
            let data = summaryToday && Array.isArray(summaryToday) && summaryToday.length > 0
                ? summaryToday
                : sampleRemarkData;

            // Debug logging
            console.log("=== REMARK WISE DATA ===");
            console.log("API Response:", summaryToday);
            console.log("Total records:", data.length);
            console.log("Sample records:", data.slice(0, 3));

            // Log first item to see all available fields
            if (data.length > 0) {
                console.log("First item keys:", Object.keys(data[0]));
                console.log("First item full object:", data[0]);
            }

            // Handle if API returns nested structure
            if (data.length > 0 && data[0]?.data) {
                data = data[0].data;
                console.log("Detected nested data structure, extracting inner data");
                if (data.length > 0) {
                    console.log("Nested first item keys:", Object.keys(data[0]));
                }
            }

            // Group data by defect type and date
            const defectMap = {};
            const dateSet = new Set();

            // Auto-detect field names from first item
            let defectField = 'defect';
            let dateField = 'created_date';
            let reelField = 'reel_qty';
            let bundleField = 'bundle_qty';

            if (data.length > 0) {
                const firstItem = data[0];
                const keys = Object.keys(firstItem).map(k => k.toLowerCase());

                // Detect defect field
                defectField = Object.keys(firstItem).find(k =>
                    k.toLowerCase().includes('defect') ||
                    k.toLowerCase().includes('remark') ||
                    k.toLowerCase().includes('fault')
                ) || 'defect';

                // Detect date field
                dateField = Object.keys(firstItem).find(k =>
                    k.toLowerCase().includes('date') ||
                    k.toLowerCase().includes('time')
                ) || 'created_date';

                // Detect reel qty field
                reelField = Object.keys(firstItem).find(k =>
                    k.toLowerCase().includes('reel_qty') ||
                    k.toLowerCase().includes('reel') ||
                    k.toLowerCase().includes('reelqty')
                ) || 'reel_qty';

                // Detect bundle qty field
                bundleField = Object.keys(firstItem).find(k =>
                    k.toLowerCase().includes('bundle_qty') ||
                    k.toLowerCase().includes('bundle') ||
                    k.toLowerCase().includes('bundleqty')
                ) || 'bundle_qty';

                console.log("Auto-detected field names:", { defectField, dateField, reelField, bundleField });
            }

            data.forEach((item, idx) => {
                const defectName = (item[defectField] || item.remark || "UNKNOWN")?.toString().toUpperCase().trim();
                const itemDate = String(item[dateField] || "01-01-2026").trim();
                const reelQty = Number(item[reelField]) || 0;
                const bundleQty = Number(item[bundleField]) || 0;

                if (idx < 3) {
                    console.log(`Item ${idx}:`, { defectName, itemDate, reelQty, bundleQty });
                }

                if (!defectName) return; // Skip items without defect name

                if (!defectMap[defectName]) {
                    defectMap[defectName] = {};
                }

                dateSet.add(itemDate);

                if (!defectMap[defectName][itemDate]) {
                    defectMap[defectName][itemDate] = { reel_qty: 0, bundle_qty: 0 };
                }

                defectMap[defectName][itemDate].reel_qty += reelQty;
                defectMap[defectName][itemDate].bundle_qty += bundleQty;
            });

            console.log("Unique dates found:", Array.from(dateSet).length);
            console.log("Dates:", Array.from(dateSet));
            console.log("Defects found:", Object.keys(defectMap));

            const sortedDates = Array.from(dateSet).sort((a, b) => {
                try {
                    // Handle different date formats: DD-MM-YYYY, YYYY-MM-DD, etc.
                    let dateA, dateB;

                    if (a.includes('-')) {
                        const parts = a.split('-');
                        if (parts[0].length === 4) {
                            // YYYY-MM-DD format
                            dateA = new Date(a);
                        } else if (parts[2].length === 4) {
                            // DD-MM-YYYY format
                            dateA = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        }
                    } else {
                        dateA = new Date(a);
                    }

                    if (b.includes('-')) {
                        const parts = b.split('-');
                        if (parts[0].length === 4) {
                            // YYYY-MM-DD format
                            dateB = new Date(b);
                        } else if (parts[2].length === 4) {
                            // DD-MM-YYYY format
                            dateB = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        }
                    } else {
                        dateB = new Date(b);
                    }

                    return dateA - dateB;
                } catch (error) {
                    console.error("Date sorting error:", error);
                    return 0;
                }
            });

            // If we only have one date or dates are missing, generate date range from fromDate to toDate
            let finalDates = sortedDates;
            if (sortedDates.length <= 1 && fromDate && toDate) {
                console.log("Only one date found, generating date range from filter");
                finalDates = generateDateRange(fromDate, toDate);
                console.log("Generated dates:", finalDates);
            }

            setAvailableDates(finalDates);

            // Transform data into table rows using final dates
            const transformedRows = Object.keys(defectMap).map(defectName => {
                const row = { defect: defectName };
                let totalReel = 0;
                let totalBundle = 0;

                finalDates.forEach(date => {
                    const dateData = defectMap[defectName][date] || { reel_qty: 0, bundle_qty: 0 };
                    row[`${date}_reel`] = dateData.reel_qty || 0;
                    row[`${date}_bundle`] = dateData.bundle_qty || 0;
                    totalReel += (dateData.reel_qty || 0);
                    totalBundle += (dateData.bundle_qty || 0);
                });

                row.totalReel = totalReel;
                row.totalBundle = totalBundle;
                row.total = totalReel + totalBundle;

                return row;
            });

            // Add total row at top
            const totalRow = { defect: "TOTAL", isTotal: true };
            let grandTotalReel = 0;
            let grandTotalBundle = 0;

            finalDates.forEach(date => {
                let dateReel = 0;
                let dateBundle = 0;
                transformedRows.forEach(row => {
                    dateReel += row[`${date}_reel`] || 0;
                    dateBundle += row[`${date}_bundle`] || 0;
                });
                totalRow[`${date}_reel`] = dateReel;
                totalRow[`${date}_bundle`] = dateBundle;
                grandTotalReel += dateReel;
                grandTotalBundle += dateBundle;
            });

            totalRow.totalReel = grandTotalReel;
            totalRow.totalBundle = grandTotalBundle;
            totalRow.total = grandTotalReel + grandTotalBundle;

            console.log("Final rows count:", transformedRows.length);
            console.log("Available dates:", finalDates);
            console.log("Defect map:", defectMap);

            setRows([...transformedRows, totalRow]);
        } catch (error) {
            console.error("Failed to transform remark data:", error);
            setRows([]);
        }
    }, [summaryToday, isLoading]);

    /* Sorting */
    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...rows].sort((a, b) => {
            if (a.isTotal) return 1; // Keep total row at bottom
            if (b.isTotal) return -1;

            const aVal = a[key];
            const bVal = b[key];

            const aNum = typeof aVal === "string" ? Number(aVal) : aVal;
            const bNum = typeof bVal === "string" ? Number(bVal) : bVal;

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }
            if (typeof aVal === "string" && typeof bVal === "string") {
                return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return 0;
        });

        setRows(sorted);
    };

    /* Column visibility */
    const handleColumnToggle = (name) => {
        setVisibleColumns((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const handleColumnMenuOpen = (e) => setColumnMenuAnchor(e.currentTarget);
    const handleColumnMenuClose = () => setColumnMenuAnchor(null);

    /* Export Excel */
    const handleExportExcel = () => {
        setExportLoading(true);
        try {
            const headerRow1 = [""];
            const headerRow2 = [];
            const numericColumnIndices = [];
            let colIndex = 0;

            if (visibleColumns.Defect) {
                headerRow2.push("Defect");
                colIndex++;
            }

            availableDates.forEach(date => {
                headerRow1.push(date);
                headerRow1.push("");
            });

            if (visibleColumns["Reel Qty"]) {
                availableDates.forEach(() => {
                    headerRow2.push("Reel Qty");
                    numericColumnIndices.push(colIndex);
                    colIndex++;
                });
            }
            if (visibleColumns["Bundle Qty"]) {
                availableDates.forEach(() => {
                    headerRow2.push("Bundle Qty");
                    numericColumnIndices.push(colIndex);
                    colIndex++;
                });
            }
            if (visibleColumns.Total) {
                headerRow2.push("Total Reel");
                numericColumnIndices.push(colIndex);
                colIndex++;
                headerRow2.push("Total Bundle");
                numericColumnIndices.push(colIndex);
                colIndex++;
                headerRow2.push("Grand Total");
                numericColumnIndices.push(colIndex);
                colIndex++;
            }

            const dataRows = rows.map(r => {
                const row = [];
                if (visibleColumns.Defect) row.push(r.defect);

                availableDates.forEach(date => {
                    if (visibleColumns["Reel Qty"]) row.push(Number(r[`${date}_reel`] || 0));
                    if (visibleColumns["Bundle Qty"]) row.push(Number(r[`${date}_bundle`] || 0));
                });

                if (visibleColumns.Total) {
                    row.push(Number(r.totalReel || 0));
                    row.push(Number(r.totalBundle || 0));
                    row.push(Number(r.total || 0));
                }

                return row;
            });

            const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);
            ws["!cols"] = Array(headerRow2.length).fill({ wch: 14 });

            // Format numeric columns as numbers with 2 decimal places
            numericColumnIndices.forEach(colIdx => {
                const colLetter = XLSX.utils.encode_col(colIdx);
                // Format data rows as numbers with 2 decimal format (skipping header rows 1-2)
                for (let rowIdx = 0; rowIdx < dataRows.length; rowIdx++) {
                    const cellRef = `${colLetter}${rowIdx + 3}`;
                    if (ws[cellRef] !== undefined) {
                        ws[cellRef] = {
                            v: ws[cellRef].v || dataRows[rowIdx][colIdx],
                            t: 'n',
                            z: '0.00'
                        };
                    }
                }
            });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Remark Report");
            XLSX.writeFile(wb, `RemarkWiseReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setExportLoading(false);
        }
    };

    /* Export PDF */
    const handleExportPDF = () => {
        setExportLoading(true);
        try {
            const doc = new jsPDF("l", "mm", "a4");
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(14);
            doc.text("Remark Wise Production Report", pageWidth / 2, 12, { align: "center" });
            doc.setFontSize(9);
            doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 18, { align: "center" });

            const headerRow = [];
            if (visibleColumns.Defect) headerRow.push("Defect");

            availableDates.forEach(date => {
                if (visibleColumns["Reel Qty"]) headerRow.push(`${date}\nReel Qty`);
                if (visibleColumns["Bundle Qty"]) headerRow.push(`${date}\nBundle Qty`);
            });

            if (visibleColumns.Total) {
                headerRow.push("Total Reel");
                headerRow.push("Total Bundle");
                headerRow.push("Grand Total");
            }

            const bodyRows = rows.map(r => {
                const row = [];
                if (visibleColumns.Defect) row.push(r.defect);

                availableDates.forEach(date => {
                    if (visibleColumns["Reel Qty"]) row.push(Number(r[`${date}_reel`] || 0).toFixed(3));
                    if (visibleColumns["Bundle Qty"]) row.push(Number(r[`${date}_bundle`] || 0).toFixed(3));
                });

                if (visibleColumns.Total) {
                    row.push(Number(r.totalReel || 0).toFixed(3));
                    row.push(Number(r.totalBundle || 0).toFixed(3));
                    row.push(Number(r.total || 0).toFixed(3));
                }

                return row;
            });

            doc.autoTable({
                head: [headerRow],
                body: bodyRows,
                startY: 24,
                theme: "grid",
                headStyles: {
                    fillColor: mode === "dark" ? [31, 42, 64] : [179, 229, 252],
                    textColor: mode === "dark" ? 255 : 0,
                    fontStyle: "bold",
                    fontSize: 8,
                    halign: "center",
                    valign: "middle",
                },
                bodyStyles: {
                    fillColor: mode === "dark" ? [33, 33, 33] : [250, 250, 250],
                    textColor: mode === "dark" ? 255 : 0,
                    fontSize: 8,
                    halign: "right",
                },
                alternateRowStyles: {
                    fillColor: mode === "dark" ? [55, 55, 55] : [255, 255, 255],
                },
                margin: { left: 8, right: 8, top: 24 },
                didDrawPage: (data) => {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        pageWidth / 2,
                        doc.internal.pageSize.height - 8,
                        { align: "center" }
                    );
                },
            });

            doc.save(`RemarkWiseReport_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error("PDF export failed:", error);
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header with filters and actions */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box component="h2" sx={{ m: 0 }}>
                        Remark Wise Production Report
                    </Box>
                    <TextField
                        type="date"
                        size="small"
                        label="From"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="To"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => {
                            let company = localStorage.getItem("companyid");
                            fetchTodayProductionSummary({
                                company,
                                fromDate,
                                toDate,
                            });
                        }}
                    >
                        Apply
                    </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Export Excel">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "Excel"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Export PDF">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportPDF}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "PDF"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Show / Hide Columns">
                        <IconButton size="small" onClick={handleColumnMenuOpen}>
                            <ViewWeekIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={columnMenuAnchor}
                        open={Boolean(columnMenuAnchor)}
                        onClose={handleColumnMenuClose}
                    >
                        {["Defect", "Reel Qty", "Bundle Qty", "Total"].map((name) => (
                            <MenuItem key={name} onClick={() => handleColumnToggle(name)}>
                                <Checkbox checked={visibleColumns[name]} size="small" />
                                {name}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Tooltip title="Close">
                        <IconButton size="small" onClick={handleBackWinderMachineEntry}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <Divider />

            {/* Table */}
            <Box sx={{ overflowX: "auto" }}>
                <CustomTableContainer colors={colors}>
                    {isLoading ? (
                        <SkeletonTableLoader rows={8} columns={12} />
                    ) : (
                        <Table sx={{ minWidth: 1200 }}>
                            <TableHead>
                                {/* Date group header row */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                    <TableCell />
                                    {availableDates.map(date => (
                                        visibleColumns["Reel Qty"] || visibleColumns["Bundle Qty"] ? (
                                            <TableCell
                                                key={date}
                                                colSpan={
                                                    (visibleColumns["Reel Qty"] ? 1 : 0) +
                                                    (visibleColumns["Bundle Qty"] ? 1 : 0)
                                                }
                                                align="center"
                                                sx={{ bgcolor: tableColors.dateGroup, fontWeight: 600 }}
                                            >
                                                {date}
                                            </TableCell>
                                        ) : null
                                    ))}
                                    {visibleColumns.Total && (
                                        <TableCell
                                            colSpan={3}
                                            align="center"
                                            sx={{ bgcolor: tableColors.dateGroup, fontWeight: 600 }}
                                        >
                                            Totals
                                        </TableCell>
                                    )}
                                </TableRow>

                                {/* Column headers */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                    {visibleColumns.Defect && (
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Defect
                                                <IconButton size="small" onClick={() => handleSort("defect")}>
                                                    <SortIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}

                                    {availableDates.flatMap(date => {
                                        const cells = [];
                                        if (visibleColumns["Reel Qty"]) {
                                            cells.push(
                                                <TableCell key={`${date}_reel_h`} sx={{ bgcolor: tableColors.headerBg, fontWeight: 500 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        Reel
                                                        <IconButton size="small" onClick={() => handleSort(`${date}_reel`)}>
                                                            <SortIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            );
                                        }
                                        if (visibleColumns["Bundle Qty"]) {
                                            cells.push(
                                                <TableCell key={`${date}_bundle_h`} sx={{ bgcolor: tableColors.headerBg, fontWeight: 500 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        Bundle
                                                        <IconButton size="small" onClick={() => handleSort(`${date}_bundle`)}>
                                                            <SortIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            );
                                        }
                                        return cells;
                                    })}

                                    {visibleColumns.Total && (
                                        <>
                                            <TableCell sx={{ bgcolor: tableColors.headerBg, fontWeight: 600 }}>
                                                Total Reel
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: tableColors.headerBg, fontWeight: 600 }}>
                                                Total Bundle
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: tableColors.headerBg, fontWeight: 600 }}>
                                                Grand Total
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((row, idx) => (
                                    <TableRow
                                        key={idx}
                                        sx={{
                                            bgcolor: row.isTotal ? tableColors.dateGroup : tableColors.rowBg,
                                            fontWeight: row.isTotal ? 600 : 400,
                                        }}
                                    >
                                        {visibleColumns.Defect && (
                                            <TableCell
                                                sx={{
                                                    bgcolor: tableColors.defectCol,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {row.defect}
                                            </TableCell>
                                        )}

                                        {availableDates.flatMap(date => {
                                            const cells = [];
                                            if (visibleColumns["Reel Qty"]) {
                                                cells.push(
                                                    <TableCell key={`${date}_reel_${idx}`}>
                                                        {Number(row[`${date}_reel`] || 0).toFixed(3)}
                                                    </TableCell>
                                                );
                                            }
                                            if (visibleColumns["Bundle Qty"]) {
                                                cells.push(
                                                    <TableCell key={`${date}_bundle_${idx}`}>
                                                        {Number(row[`${date}_bundle`] || 0).toFixed(3)}
                                                    </TableCell>
                                                );
                                            }
                                            return cells;
                                        })}

                                        {visibleColumns.Total && (
                                            <>
                                                <TableCell sx={{ bgcolor: tableColors.totalCol, fontWeight: 600 }}>
                                                    {Number(row.totalReel || 0).toFixed(3)}
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: tableColors.totalCol, fontWeight: 600 }}>
                                                    {Number(row.totalBundle || 0).toFixed(3)}
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: tableColors.totalCol, fontWeight: 600 }}>
                                                    {Number(row.total || 0).toFixed(3)}
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CustomTableContainer>
            </Box>
        </Box>
    );
}
